(() => {
  const root = document.querySelector('[data-fvpd-root]');
  if (!root || root.dataset.fvpdReady) return;
  root.dataset.fvpdReady = 'true';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const gallery = root.querySelector('[data-gallery]');

  if (gallery) {
    const stage = gallery.querySelector('.vv-gallery-stage');
    const slides = [...gallery.querySelectorAll('[data-slide]')];
    const dots = [...gallery.querySelectorAll('[data-dot]')];
    const current = gallery.querySelector('[data-current]');
    const progress = gallery.querySelector('[data-progress]');
    let index = 0;
    let timer = null;
    let resumeTimer = null;
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let activePointer = null;
    let galleryVisible = true;

    const stopAuto = () => {
      clearInterval(timer);
      timer = null;
      if (progress) progress.classList.remove('vv-is-running');
    };

    const restartProgress = () => {
      if (!progress || reduceMotion || !galleryVisible || document.hidden) return;
      progress.classList.remove('vv-is-running');
      requestAnimationFrame(() => requestAnimationFrame(() => progress.classList.add('vv-is-running')));
    };

    const ensureImage = slide => {
      const image = slide?.querySelector('img[data-src]');
      if (!image) return;
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    };

    const show = (next, manual = false) => {
      index = (next + slides.length) % slides.length;
      ensureImage(slides[index]);
      slides.forEach((slide, i) => slide.classList.toggle('vv-is-active', i === index));
      dots.forEach((dot, i) => {
        dot.classList.toggle('vv-is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (current) current.textContent = String(index + 1).padStart(2, '0');
      restartProgress();
      if (manual) pauseThenResume();
    };

    const startAuto = () => {
      if (reduceMotion || document.hidden || !galleryVisible) return;
      clearInterval(timer);
      timer = setInterval(() => show(index + 1), 2500);
      restartProgress();
    };

    const pauseThenResume = () => {
      stopAuto();
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(startAuto, 6500);
    };

    const finishSwipe = (endX, endY) => {
      const dx = endX - startX;
      const dy = endY - startY;
      if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy)) show(index + (dx < 0 ? 1 : -1), true);
    };

    gallery.querySelector('[data-prev]')?.addEventListener('click', () => show(index - 1, true));
    gallery.querySelector('[data-next]')?.addEventListener('click', () => show(index + 1, true));
    dots.forEach(dot => dot.addEventListener('click', () => show(Number(dot.dataset.dot), true)));

    if ('PointerEvent' in window) {
      stage.addEventListener('pointerdown', event => {
        if (event.isPrimary === false) return;
        startX = event.clientX;
        startY = event.clientY;
        dragging = true;
        activePointer = event.pointerId;
        pauseThenResume();
      });
      stage.addEventListener('pointerup', event => {
        if (!dragging || (activePointer !== null && event.pointerId !== activePointer)) return;
        dragging = false;
        activePointer = null;
        finishSwipe(event.clientX, event.clientY);
      });
      stage.addEventListener('pointercancel', () => { dragging = false; activePointer = null; });
    } else {
      stage.addEventListener('touchstart', event => {
        const touch = event.changedTouches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        pauseThenResume();
      }, { passive: true });
      stage.addEventListener('touchend', event => {
        const touch = event.changedTouches[0];
        finishSwipe(touch.clientX, touch.clientY);
      }, { passive: true });
    }

    stage.addEventListener('dragstart', event => event.preventDefault());
    stage.tabIndex = 0;
    stage.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') show(index - 1, true);
      if (event.key === 'ArrowRight') show(index + 1, true);
    });

    const galleryObserver = new IntersectionObserver(entries => {
      galleryVisible = entries[0].isIntersecting;
      if (galleryVisible) startAuto(); else stopAuto();
    }, { threshold: 0.18 });
    galleryObserver.observe(gallery);
    document.addEventListener('visibilitychange', () => document.hidden ? stopAuto() : startAuto());
    show(0);
  }

  root.querySelectorAll('.vv-quantity').forEach(quantity => {
    const input = quantity.querySelector('input');
    quantity.querySelector('[data-qty="minus"]')?.addEventListener('click', () => {
      input.value = Math.max(1, Number(input.value || 1) - 1);
    });
    quantity.querySelector('[data-qty="plus"]')?.addEventListener('click', () => {
      input.value = Math.max(1, Number(input.value || 1) + 1);
    });
    input.addEventListener('change', () => { input.value = Math.max(1, Number(input.value || 1)); });
  });

  const videos = [...root.querySelectorAll('[data-story-video]')];
  const updateToggle = video => {
    const button = video.closest('.vv-motion-card')?.querySelector('[data-video-toggle]');
    if (!button) return;
    button.textContent = video.paused ? 'PLAY' : 'PAUSE';
    button.setAttribute('aria-label', `${video.paused ? 'Play' : 'Pause'} ${video.getAttribute('aria-label') || 'product video'}`);
  };
  const hydrate = video => {
    if (video.dataset.mediaReady) return;
    video.querySelectorAll('source[data-src]').forEach(source => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.mediaReady = 'true';
    video.autoplay = true;
    video.defaultMuted = true;
    video.muted = true;
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.load();
  };
  const play = video => {
    if (document.hidden || reduceMotion) return;
    hydrate(video);
    if (video.dataset.inView !== 'true') return;
    video.autoplay = true;
    video.defaultMuted = true;
    video.muted = true;
    const promise = video.play();
    if (promise) {
      promise.then(() => {
        video.dataset.autoplayBlocked = 'false';
        updateToggle(video);
      }).catch(() => {
        video.dataset.autoplayBlocked = 'true';
        updateToggle(video);
      });
    }
  };
  if (videos.length) {
    const hydrationObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          hydrate(entry.target);
          hydrationObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '280px 0px', threshold: 0 });
    const playbackObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const video = entry.target;
        const shouldPlay = entry.isIntersecting && entry.intersectionRatio >= 0.08;
        video.dataset.inView = shouldPlay ? 'true' : 'false';
        if (shouldPlay) play(video);
        else { video.pause(); updateToggle(video); }
      });
    }, { rootMargin: '90px 0px', threshold: [0, 0.08, 0.3, 0.65, 1] });
    videos.forEach(video => {
      video.autoplay = true;
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.addEventListener('loadeddata', () => {
        if (video.dataset.inView === 'true') play(video);
      });
      video.addEventListener('canplay', () => {
        if (video.dataset.inView === 'true' && video.paused) play(video);
      });
      hydrationObserver.observe(video);
      playbackObserver.observe(video);
      updateToggle(video);
    });
    root.querySelectorAll('[data-video-toggle]').forEach(button => {
      button.addEventListener('click', () => {
        const video = button.closest('.vv-motion-card').querySelector('video');
        if (video.paused) play(video); else { video.pause(); updateToggle(video); }
      });
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) videos.forEach(video => { video.pause(); updateToggle(video); });
      else videos.forEach(video => { if (video.dataset.inView === 'true') play(video); });
    });
    const retryVisibleVideos = () => {
      videos.forEach(video => {
        if (video.dataset.inView === 'true' && video.paused) play(video);
      });
    };
    document.addEventListener('pointerdown', retryVisibleVideos, { passive: true });
    document.addEventListener('touchstart', retryVisibleVideos, { passive: true });
  }

  const sticky = root.querySelector('.vv-mobile-sticky');
  const purchaseAnchor = root.querySelector('[data-purchase-anchor]');
  if (sticky && purchaseAnchor) {
    const stickyObserver = new IntersectionObserver(entries => {
      sticky.classList.toggle('vv-mobile-sticky--visible', !entries[0].isIntersecting);
    }, { threshold: 0.08 });
    stickyObserver.observe(purchaseAnchor);
  }

  root.querySelector('.vv-shop-pay')?.addEventListener('click', event => {
    event.preventDefault();
    root.querySelector('.vv-add-button')?.focus();
  });
})();
